import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { UsersIcon, CheckCircleIcon, ArrowRightIcon, ChevronRightIcon, LayersIcon, TargetIcon, CalendarIcon, TrendingUpIcon, ShieldCheckIcon, ClipboardListIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { ContactForm } from '../../components/ContactForm';
import { HeroLeadForm } from '../../components/HeroLeadForm';
import { ClientLogosStrip } from '../../components/ClientLogosStrip';
import { TestimonialsSection } from '../../components/TestimonialsSection';
import { FaqSection } from '../../components/FaqSection';
import { useSEO } from '../../hooks/useSEO';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslations } from '../../i18n/translations';
const seoData = {
  UA: {
    title: 'Комплектація команди — Побудова ефективних команд | ESCA',
    description: 'Комплексна комплектація команд під ваш проєкт. Підберемо від 2 до 20+ спеціалістів з урахуванням культури компанії та специфіки бізнесу.',
    keywords: 'комплектація команди, team building, побудова команди, підбір команди, HR, ESCA'
  },
  RU: {
    title: 'Комплектация команды — Построение эффективных команд | ESCA',
    description: 'Комплексная комплектация команд под ваш проект. Подберём от 2 до 20+ специалистов с учётом культуры компании и специфики бизнеса.',
    keywords: 'комплектация команды, team building, построение команды, подбор команды, HR, ESCA'
  },
  EN: {
    title: 'Team Building — Build High-Performing Teams | ESCA',
    description: 'Comprehensive team staffing for your project. We recruit 2 to 20+ specialists considering your company culture and business specifics.',
    keywords: 'team building, team staffing, team recruitment, HR, ESCA'
  }
};
function HeroSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  return <section className="relative pt-14 lg:pt-16 pb-12 lg:pb-14 overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-navy-900" data-id="element-1267" data-mp="sp5twbfcnfxv">
      <div className="absolute inset-0" data-id="element-1268" data-mp="xiik2pzofusq">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1269" data-mp="jq2crczqtam0" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-3xl hidden sm:block" data-id="element-1270" data-mp="oovmqvftmjhf" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,1) 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-1271" data-mp="1cc3tlhswe0lk" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-1272" data-mp="17daexm1vk3jt">
        <motion.nav initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="flex items-center gap-1.5 text-sm text-primary-300/70 mb-6" data-id="element-1273" data-mp="3n5q3kh7d79nm">
          <Link to="/" className="hover:text-primary-300 transition-colors" data-id="element-1274" data-mp="3flyvlkoq4knh">
            {t.nav.home}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1275" data-mp="3tk5f1vsxei3m" />
          <Link to="/#services" className="hover:text-primary-300 transition-colors" data-id="element-1276" data-mp="3oo83e2slxzx6">
            {t.nav.services}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1277" data-mp="2ww8y2ptktkrv" />
          <span className="text-primary-200" data-id="element-1278" data-mp="2wxk3eu2o70l3">{t.teamBuilding.title}</span>
        </motion.nav>

        <div className="max-w-4xl" data-id="element-1279" data-mp="3aoym0zso0zrm">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.05
        }} className="flex items-center gap-3 mb-5" data-id="element-1280" data-mp="35wnmhy1u7fzl">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-400 to-cyan-500 text-white shadow-xl shadow-primary-500/30" data-id="element-1281" data-mp="2qw1kcyc3s3nn">
              <UsersIcon className="w-5 h-5" data-id="element-1282" data-mp="30rv87dv9vjkq" />
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary-500/20 text-primary-300 text-xs font-semibold rounded-full border border-primary-500/20" data-id="element-1283" data-mp="35jmpsywgaeyq">
              {t.teamBuilding.badge}
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.08]" data-id="element-1284" data-mp="3ej0362np3noq">
            {t.teamBuilding.h1_1}{' '}
            <span className="text-cyan-400" data-id="element-1285" data-mp="b93aq05kjnb7">{t.teamBuilding.h1_accent}</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.18
        }} className="mt-4 text-base sm:text-lg text-primary-200/80 leading-relaxed" data-id="element-1286" data-mp="6ivak91s990k">
            {t.teamBuilding.subtitle}
          </motion.p>
        </div>

        <div className="mt-6 lg:mt-8" data-id="element-1287" data-mp="q745gqupnt3r">
          <HeroLeadForm positions={t.teamBuilding.formPositions} source="team_building_hero" data-id="element-1288" data-mp="l79b2dbq8ldy" />
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-1289" data-mp="g7mze8bjrd79">
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1290" data-mp="37exch8iywx8i">
            <div className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-1291" data-mp="2tf4q1peh536c">
              {t.teamBuilding.stats.time_val}
            </div>
            <div className="text-xs text-primary-300/70 mt-1" data-id="element-1292" data-mp="2yd694abwh2ht">
              {t.teamBuilding.stats.time_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1293" data-mp="2jn8fvfz45mlr">
            <div className="text-2xl sm:text-3xl font-extrabold text-cyan-400" data-id="element-1294" data-mp="2oi4kdflje2fr">
              {t.teamBuilding.stats.teams_val}
            </div>
            <div className="text-xs text-primary-300/70 mt-1" data-id="element-1295" data-mp="2epgoqo6am4im">
              {t.teamBuilding.stats.teams_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1296" data-mp="20r8syjkzt991">
            <div className="text-2xl sm:text-3xl font-extrabold text-white" data-id="element-1297" data-mp="25os77jhoxm0z">
              {t.teamBuilding.stats.guarantee_val}
            </div>
            <div className="text-xs text-primary-300/70 mt-1" data-id="element-1298" data-mp="1h56ytatmokpi">
              {t.teamBuilding.stats.guarantee_label}
            </div>
          </div>
          <div className="p-4 sm:p-5 rounded-2xl bg-white/5 border border-white/10 text-center" data-id="element-1299" data-mp="1w397fzfinwdm">
            <div className="text-2xl sm:text-3xl font-extrabold text-cyan-400" data-id="element-1300" data-mp="1r1jrc5dfssio">
              {t.teamBuilding.stats.size_val}
            </div>
            <div className="text-xs text-primary-300/70 mt-1" data-id="element-1301" data-mp="203t5d57wz40t">
              {t.teamBuilding.stats.size_label}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function WhatIsIncluded() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-white" ref={ref} data-id="element-1302" data-mp="2jsjygp72ecek">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1303" data-mp="3q6gp5ezid5u2">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1304" data-mp="3q5yh7l3hh36b">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1305" data-mp="37h1ovmrnckqq">
            {t.teamBuilding.whatIsIncluded.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-1306" data-mp="3ggosmf7um2my">
            {t.teamBuilding.whatIsIncluded.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto" data-id="element-1307" data-mp="3l8oonwkssbe9">
            {t.teamBuilding.whatIsIncluded.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1308" data-mp="2sqazsmhfvzrb">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1309" data-mp="2l7l3nwnn219y">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1310" data-mp="22a0gqv4fpcrz">
              <LayersIcon className="w-5 h-5" data-id="element-1311" data-mp="1sjp8iwdpwhfa" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1312" data-mp="1ohuac0r32k5q">
              {t.teamBuilding.whatIsIncluded.items[0].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1313" data-mp="1jhoc21qb5zeo">
              {t.teamBuilding.whatIsIncluded.items[0].desc}
            </p>
            <ul className="space-y-2" data-id="element-1314" data-mp="9j53zspxu6q4">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1315" data-mp="9hu443zm5j4a">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1316" data-mp="5i3377gdhmm1" />
                {t.teamBuilding.whatIsIncluded.items[0].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1317" data-mp="e9vchetnupp1">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1318" data-mp="ebyc8qkm7x6y" />
                {t.teamBuilding.whatIsIncluded.items[0].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1319" data-mp="3ipenei443crr">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1320" data-mp="iqkmywjb1p9" />
                {t.teamBuilding.whatIsIncluded.items[0].points[2]}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1321" data-mp="1bwklw9qp04kx">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1322" data-mp="2kuam00nsn7ix">
              <TargetIcon className="w-5 h-5" data-id="element-1323" data-mp="38miuas6pgab9" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1324" data-mp="3cl8f35u2ou4x">
              {t.teamBuilding.whatIsIncluded.items[1].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1325" data-mp="33tg6z1sitx3i">
              {t.teamBuilding.whatIsIncluded.items[1].desc}
            </p>
            <ul className="space-y-2" data-id="element-1326" data-mp="3rhzehf3qy5uj">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1327" data-mp="3rgxv8nckud1q">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1328" data-mp="5wbppod0xie" />
                {t.teamBuilding.whatIsIncluded.items[1].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1329" data-mp="50yzlu2d39li">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1330" data-mp="1vzmo18ntke" />
                {t.teamBuilding.whatIsIncluded.items[1].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1331" data-mp="3rata6pcp9jwl">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1332" data-mp="3n8eu2d0f0y90" />
                {t.teamBuilding.whatIsIncluded.items[1].points[2]}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1333" data-mp="3dexxj18w951n">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1334" data-mp="1iziph12655fg">
              <ClipboardListIcon className="w-5 h-5" data-id="element-1335" data-mp="1sz9lj76dnshn" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1336" data-mp="vb9bumf3znze">
              {t.teamBuilding.whatIsIncluded.items[2].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1337" data-mp="10b40skfbq3ht">
              {t.teamBuilding.whatIsIncluded.items[2].desc}
            </p>
            <ul className="space-y-2" data-id="element-1338" data-mp="157m1983mffoc">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1339" data-mp="1a0f5luv6glzw">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1340" data-mp="cl1ao9uyz7lu" />
                {t.teamBuilding.whatIsIncluded.items[2].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1341" data-mp="hhrqcw2v416q">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1342" data-mp="2j9jrjhsj7an3" />
                {t.teamBuilding.whatIsIncluded.items[2].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1343" data-mp="2sz3aeng8v4hy">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1344" data-mp="2o8d7rhz7dfqy" />
                {t.teamBuilding.whatIsIncluded.items[2].points[2]}
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.25
        }} className="p-8 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-1345" data-mp="2xyfz7dqoeln6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-lg shadow-primary-500/20" data-id="element-1346" data-mp="1cmy1n8j9k378">
              <ShieldCheckIcon className="w-5 h-5" data-id="element-1347" data-mp="3t68its1sl76y" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1348" data-mp="3fek21bgllujn">
              {t.teamBuilding.whatIsIncluded.items[3].title}
            </h3>
            <p className="text-navy-500 leading-relaxed mb-4" data-id="element-1349" data-mp="3jeub4wzot12q">
              {t.teamBuilding.whatIsIncluded.items[3].desc}
            </p>
            <ul className="space-y-2" data-id="element-1350" data-mp="3agscgo3fekim">
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1351" data-mp="3ahtrt4rh5t65">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1352" data-mp="2wi2fd8glwku9" />
                {t.teamBuilding.whatIsIncluded.items[3].points[0]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1353" data-mp="uoutfg6hn4ze">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1354" data-mp="yqzgfw2fl3d2" />
                {t.teamBuilding.whatIsIncluded.items[3].points[1]}
              </li>
              <li className="flex items-center gap-2 text-sm text-navy-600" data-id="element-1355" data-mp="27p7qtnj14l1z">
                <CheckCircleIcon className="w-4 h-4 text-primary-500 flex-shrink-0" data-id="element-1356" data-mp="22o1xpolgivrx" />
                {t.teamBuilding.whatIsIncluded.items[3].points[2]}
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CasesSection() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 lg:py-28 bg-navy-50/40" id="cases" ref={ref} data-id="element-1357" data-mp="2hlaooh6ulxj4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1358" data-mp="2cn9760p2m69r">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-1359" data-mp="2cmr4ombtevte">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-1360" data-mp="3hmpaftqi54kg">
            {t.teamBuilding.cases.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-navy-900 tracking-tight" data-id="element-1361" data-mp="3qmm6whj4v7q2">
            {t.teamBuilding.cases.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1362" data-mp="3vjkyasbex0kk">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="p-7 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1363" data-mp="4ecjgqss5e2t">
            <div className="flex items-center gap-2 mb-4" data-id="element-1364" data-mp="18gaury629wgk">
              <CalendarIcon className="w-4 h-4 text-primary-500" data-id="element-1365" data-mp="18ix15uh7u5x7" />
              <span className="text-xs font-semibold text-navy-400 uppercase tracking-wide" data-id="element-1366" data-mp="13gxx0idnj4z7">
                {t.teamBuilding.cases.items[0].badge}
              </span>
            </div>
            <div className="text-4xl font-extrabold text-primary-600 mb-1" data-id="element-1367" data-mp="1dfneia1mynex">
              {t.teamBuilding.cases.items[0].val}
            </div>
            <div className="text-sm font-semibold text-navy-700 mb-3" data-id="element-1368" data-mp="po8bef21c22m">
              {t.teamBuilding.cases.items[0].val_label}
            </div>
            <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1369" data-mp="zgnph1a7o6i8">
              {t.teamBuilding.cases.items[0].desc}
            </p>
            <div className="mt-4 pt-4 border-t border-navy-100" data-id="element-1370" data-mp="unupeqpotmch">
              <div className="text-xs text-emerald-600 font-semibold" data-id="element-1371" data-mp="6z1oefu7kob0">
                {t.teamBuilding.cases.items[0].result}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="p-7 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1372" data-mp="2dodf4xm0zwym">
            <div className="flex items-center gap-2 mb-4" data-id="element-1373" data-mp="2wcs7bo82xh10">
              <CalendarIcon className="w-4 h-4 text-primary-500" data-id="element-1374" data-mp="31cd7u32wdxfu" />
              <span className="text-xs font-semibold text-navy-400 uppercase tracking-wide" data-id="element-1375" data-mp="2neg84xt5dawy">
                {t.teamBuilding.cases.items[1].badge}
              </span>
            </div>
            <div className="text-4xl font-extrabold text-primary-600 mb-1" data-id="element-1376" data-mp="1byzwvv0a5b5r">
              {t.teamBuilding.cases.items[1].val}
            </div>
            <div className="text-sm font-semibold text-navy-700 mb-3" data-id="element-1377" data-mp="1g14n0jrirpnn">
              {t.teamBuilding.cases.items[1].val_label}
            </div>
            <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1378" data-mp="mb82df2cb4ia">
              {t.teamBuilding.cases.items[1].desc}
            </p>
            <div className="mt-4 pt-4 border-t border-navy-100" data-id="element-1379" data-mp="8c8c62z6ihk0">
              <div className="text-xs text-emerald-600 font-semibold" data-id="element-1380" data-mp="dajoqlguz65x">
                {t.teamBuilding.cases.items[1].result}
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="p-7 rounded-2xl bg-white border border-navy-100 shadow-sm" data-id="element-1381" data-mp="3uox94vc3cmr5">
            <div className="flex items-center gap-2 mb-4" data-id="element-1382" data-mp="3gqqtywex9bgt">
              <CalendarIcon className="w-4 h-4 text-primary-500" data-id="element-1383" data-mp="2x4x9z7sape4s" />
              <span className="text-xs font-semibold text-navy-400 uppercase tracking-wide" data-id="element-1384" data-mp="2x7a04887iusx">
                {t.teamBuilding.cases.items[2].badge}
              </span>
            </div>
            <div className="text-4xl font-extrabold text-primary-600 mb-1" data-id="element-1385" data-mp="371jsvr129v46">
              {t.teamBuilding.cases.items[2].val}
            </div>
            <div className="text-sm font-semibold text-navy-700 mb-3" data-id="element-1386" data-mp="3g3t0o94y3adv">
              {t.teamBuilding.cases.items[2].val_label}
            </div>
            <p className="text-sm text-navy-500 leading-relaxed" data-id="element-1387" data-mp="3c2hao2i5sn55">
              {t.teamBuilding.cases.items[2].desc}
            </p>
            <div className="mt-4 pt-4 border-t border-navy-100" data-id="element-1388" data-mp="3kv1ah6a6sxrk">
              <div className="text-xs text-emerald-600 font-semibold" data-id="element-1389" data-mp="3kvt8enr9b7l0">
                {t.teamBuilding.cases.items[2].result}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1390" data-mp="fop360j74kr4">
      <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-primary-900 via-primary-800 to-navy-900 relative overflow-hidden" data-id="element-1391" data-mp="tf114g2gpdq6">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl hidden sm:block" data-id="element-1392" data-mp="13errc5jzp6kg" />
        <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl hidden sm:block" data-id="element-1393" data-mp="307laau7d62o5" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-1394" data-mp="3577n52ja5gwf">
          <div className="flex-1" data-id="element-1395" data-mp="2xmvloy1pt9pv">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-1396" data-mp="2xnyexcmifo8y">
              {t.teamBuilding.ctaBanner.title}
            </h3>
            <p className="mt-2 text-primary-200/80 leading-relaxed" data-id="element-1397" data-mp="2nw10lx07gg60">
              {t.teamBuilding.ctaBanner.subtitle}
            </p>
          </div>
          <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-primary-900 bg-white rounded-xl hover:bg-primary-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-1398" data-mp="2ivmr0aiygy5v">
            {t.teamBuilding.ctaBanner.button}
            <ArrowRightIcon className="w-4 h-4" data-id="element-1399" data-mp="36p4xdr8pz94u" />
          </a>
        </div>
      </div>
    </motion.div>;
}
export function TeamBuildingPage() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const seo = seoData[lang];
  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords
  });
  return <div className="min-h-screen w-full bg-white overflow-x-hidden" data-id="element-1400" data-mp="3f280ey3no6zr">
      <Header data-id="element-1401" data-mp="3jtpr8tbysr70" />
      <main className="pt-16 lg:pt-20" data-id="element-1402" data-mp="3ju7vac4vc9ph">
        <HeroSection data-id="element-1403" data-mp="3jvak8eims22r" />
        <ClientLogosStrip data-id="element-1404" data-mp="3jvssk9iki6r1" />
        <WhatIsIncluded data-id="element-1405" data-mp="355les73i9bwo" />
        <CasesSection data-id="element-1406" data-mp="356ee4n04wz4s" />
        <TestimonialsSection data-id="element-1407" data-mp="356whe3l7sdm7" />
        <FaqSection items={t.teamBuilding.faqItems} data-id="element-1408" data-mp="3a3wqyoh9vyi2" />
        <CtaBanner data-id="element-1409" data-mp="3a4o9axluzzqe" />
        <div id="contact" data-id="element-1410" data-mp="3a226fb9wy8ei">
          <ContactForm data-id="element-1411" data-mp="1omj0zqpbz2d" />
        </div>
      </main>
      <Footer data-id="element-1412" data-mp="1ow7uk00g06h" />
    </div>;
}